import {UserLocal} from "@app/core/user/domain/user.local";
import {User} from "@app/core/user/domain/entities/user";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {Injectable} from "@angular/core";
import {Subject} from "rxjs";
import {Encrypt} from "@app/core/user/domain/encrypt";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";

@Injectable({
    providedIn: 'root',
})
export class UserSaver {

    private userChangedSubject = new Subject<void>();
    readonly userChanged$ = this.userChangedSubject.asObservable();

    public temporalCredentials: Credentials | null  = null;

    constructor(
        private storage: UserLocal,
        private credentialsStorage: CredentialsStorage,
    ) { }

    update(user: User) {
        this.storage.saveUser(user);
        this.credentialsStorage.update({ email: user.email });
        this.userChangedSubject.next();
    }

    /**
     * It saves the user and the password in the storage
     * @param {User} user - User - the user object that we want to save
     * @param {string} password - string - the password that the user entered
     */
    async saveWithPassword(user: User, credentials: Credentials): Promise<void> {
        const hashedPassword = await Encrypt.sha1(credentials.password);
        this.credentialsStorage.save(new Credentials(user.email, hashedPassword))
        this.storage.saveUser(user)
        this.userChangedSubject.next();
    }

    /**
     * It does the same as {@link saveWithPassword}, but it doesn't hash the password.
     * @param user
     * @param password
     */
    async saveWithEncryptedPassword(user: User, credentials: Credentials): Promise<void> {
        this.credentialsStorage.save(new Credentials(user.email, credentials.password));
        this.storage.saveUser(user);
        this.userChangedSubject.next();
    }

    /**
     * It saves the user and the password in the storage temporarily.
     * The temporal credentials will be deleted when the user logged in or when the app is closed.
     * @param credentials
     */
    async saveTemporalCredentials(credentials: Credentials): Promise<void> {
        const hashedPassword = await Encrypt.sha1(credentials.password);
        this.credentialsStorage.saveTemporalCredentials(new Credentials(credentials.email, hashedPassword));
    }

    /**
     * It does the same as {@link saveTemporalCredentials}, but it doesn't hash the password.
     * @param credentials
     */
    async saveTemporalEncryptedCredentials(credentials: Credentials) {
        this.credentialsStorage.saveTemporalCredentials(credentials);
    }
}
